<template>
  <EXTooltip v-if="ideaMDP452 && tooltipTitle" placement="top" :title="tooltipTitle" :delay="200">
    <div class="inspection-quiz-status-badge" :class="rootClass">
      <span class="inspection-quiz-status-badge__label">
        <span v-if="leadingGlyph" class="inspection-quiz-status-badge__glyph">{{ leadingGlyph }}</span>
        {{ labelText }}
      </span>
    </div>
  </EXTooltip>
  <div v-else class="inspection-quiz-status-badge" :class="rootClass">
    <span class="inspection-quiz-status-badge__label">
      <span v-if="leadingGlyph" class="inspection-quiz-status-badge__glyph">{{ leadingGlyph }}</span>
      {{ labelText }}
    </span>
  </div>
</template>

<script setup>
import { computed } from "vue";
import EXTooltip from "@components/tooltip/EXTooltip.vue";
import { formatInspectionDate } from "@components/utils/buildCheckpointDrawerSections";

/**
 * Pill badge reporting the state of a checkpoint's quiz step.
 * Read-only — no click target or action.
 *
 * ER-11856 adds a hover tooltip (`By {employee} on {quiz_attempted_on}`) shown
 * only when the quiz was actually attempted (Passed / Failed) and the server
 * exposes the facet timestamp. Not Attempted / Not Required get no tooltip.
 *
 * Not Attempted is the design default; omitting the prop renders that variant.
 */
const props = defineProps({
  /**
   * Feature flag `idea-MDP-452` (New Checkpoint Edit and Inspection). The tooltip
   * is new behaviour, so it only renders when the parent resolves the flag and
   * passes it in (kill-switch semantics matching InspectionInspectedStatusBadge).
   * The chip itself keeps rendering — only the tooltip is gated.
   */
  ideaMDP452: {
    type: Boolean,
    default: false,
  },
  quizStatus: {
    type: String,
    default: "Not Attempted",
    validator: (value) => ["Not Attempted", "Passed", "Failed", "Not Required"].includes(value),
  },
  /** Timestamp of the most recent quiz attempt (ER-11856). */
  quizAttemptedOn: {
    type: String,
    default: null,
  },
  /** Employee's own name — used for the quiz tooltip (no distinct actor). */
  employeeName: {
    type: String,
    default: "",
  },
});

const LABELS = {
  "Not Attempted": "Quiz not attempted",
  Passed: "Quiz passed",
  Failed: "Quiz failed",
  "Not Required": "Quiz not required",
};

const GLYPHS = {
  Passed: "✓",
  Failed: "✕",
};

const labelText = computed(() => LABELS[props.quizStatus] || LABELS["Not Attempted"]);
const leadingGlyph = computed(() => GLYPHS[props.quizStatus] || "");

const rootClass = computed(() => ({
  "inspection-quiz-status-badge--not-attempted": props.quizStatus === "Not Attempted",
  "inspection-quiz-status-badge--passed": props.quizStatus === "Passed",
  "inspection-quiz-status-badge--failed": props.quizStatus === "Failed",
  "inspection-quiz-status-badge--not-required": props.quizStatus === "Not Required",
}));

const tooltipTitle = computed(() => {
  if (props.quizStatus !== "Passed" && props.quizStatus !== "Failed") return "";
  const name = String(props.employeeName || "").trim();
  const date = formatInspectionDate(props.quizAttemptedOn);
  if (!name || !date) return "";
  return `By ${name} on ${date}`;
});
</script>

<style scoped>
.inspection-quiz-status-badge {
  display: inline-flex;
  align-items: center;
  width: fit-content;
  padding: 2px 8px;
  border-radius: 9999px;
  border: 1px solid transparent;
  box-sizing: border-box;
  font-family: Inter, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  color: #344054;
  white-space: nowrap;
}

.inspection-quiz-status-badge__label {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
}

.inspection-quiz-status-badge__glyph {
  display: inline-block;
  line-height: 1;
}

/* Not Attempted — shared Basic Badges base */
.inspection-quiz-status-badge--not-attempted {
  background-color: #f9fafb;
  border-color: #eaecf0;
  color: #344054;
}

/* Passed — non-base style: 9px/3px padding, Inter Semi Bold 11px, 5px glyph gap */
.inspection-quiz-status-badge--passed {
  padding: 3px 9px;
  font-size: 11px;
  font-weight: 600;
  line-height: normal;
  background-color: #ecfdf3;
  border-color: #aaefc6;
  color: #067647;
}

/* Failed — non-base style: 9px/3px padding, Inter Semi Bold 11px, 5px glyph gap */
.inspection-quiz-status-badge--failed {
  padding: 3px 9px;
  font-size: 11px;
  font-weight: 600;
  line-height: normal;
  background-color: #fef3f2;
  border-color: #fecdca;
  color: #b42318;
}

/* Not Required — shared base, no fill, heavier border */
.inspection-quiz-status-badge--not-required {
  background-color: transparent;
  border: 1.5px solid #475467;
  color: #344054;
}
</style>
